import React, { useEffect, useState } from "react";
import jsonExport from "jsonexport/dist";

import { API, graphqlOperation } from "aws-amplify";
import { searchInvoiceServicesForReport } from "../../../graphql/report-queries";
import { fetchServices } from "../../resources/services/ServiceUtils";

import {
  useMediaQuery,
  Card,
  Grid,
  Hidden,
  Typography,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  AutocompleteArrayInput,
  Button,
  Datagrid,
  DateField,
  downloadCSV,
  ListContextProvider,
  NumberField,
  required,
  SelectInput,
  SimpleForm,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useList,
  useListContext,
  useNotify,
} from "react-admin";

import { ChevronRightIcon, ExportIcon } from "../../../components/common/iconography";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { Pagination } from "../../../components/Pagination";
import { BackButton } from "../../../components/common/BackButton";
import { AccountDropDown } from "../../../components/common/AccountDropDown";
import { HelpButton } from "../../../components/common/HelpButton";
import { MonthSlider } from "../../../components/common/MonthSlider";
import { CarrierTypes } from "../../../utils/constants";
import { logError } from "../../../utils/appUtils";
import { ChargeEvent } from "../../resources/accounts/chargeRules/chargeRuleConstants";
import { createDateRangeParams, YearSelect } from "../ReportsUtils";

const useStyles = makeStyles((theme) => ({
  totals: {
    marginLeft: theme.spacing(2),
  },
  servicesAutocompleteRoot: {
    minHeight: theme.spacing(5),
    alignItems: "center",
    alignContent: "center",
    paddingTop: "2px !important",
    paddingBottom: "2px !important",
    paddingLeft: "8px !important",
    paddingRight: "8px !important",
    '&& [class*="RaAutocompleteArrayInput-chipContainerOutlined"]': {
      margin: "0 0 0 0 !important",
    },
    '&& [class*="RaAutocompleteArrayInput-chip"][class*="MuiChip-root"]': {
      height: 24,
      fontSize: "0.75rem",
    },
    '&& [class*="RaAutocompleteArrayInput-chip"] [class*="MuiChip-label"]': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    '&& [class*="RaAutocompleteArrayInput-chip"] [class*="MuiChip-deleteIcon"]':
      {
        margin: "0 2px 0 -2px",
      },
    '&& [class*="RaAutocompleteArrayInput-inputInput"]': {
      paddingTop: "1px !important",
      paddingBottom: "1px !important",
      paddingLeft: "0 !important",
    },
  },
}));

const exportList = (invoiceServices) => {
  const handleUndefined = (item) => (item ? item : "");

  const rows = [];

  invoiceServices.forEach((line) => {
    const { invoice, service } = line;

    rows.push({
      InvoiceNo: invoice?.invoiceNumber || "",
      Customer: invoice?.accountName || "",
      InvoiceDate: invoice?.createdAt
        ? new Date(invoice.createdAt).toLocaleDateString()
        : "",
      DueDate: invoice?.dueDate
        ? new Date(invoice.dueDate).toLocaleDateString()
        : "",
      Terms: invoice?.paymentTerms || "",
      Memo: handleUndefined(invoice?.notes),
      "Item(Product/Service)": service?.name || "",
      ItemDescription: handleUndefined(line.serviceNote),
      ItemQuantity: line.quantity,
      ItemRate: line.unitCost,
      ItemAmount: line.amount,
      ItemTaxCode: line.taxRate?.taxCode || "",
      ItemTaxAmount: line.itemTax || "",
      Currency: "USD",
      ServiceDate: line.serviceDate
        ? new Date(line.serviceDate).toLocaleDateString()
        : "",
    });
  });

  jsonExport(
    rows,
    {
      rename: [
        "InvoiceNo",
        "Customer",
        "InvoiceDate",
        "DueDate",
        "Terms",
        "Memo",
        "Item(Product/Service)",
        "ItemDescription",
        "ItemQuantity",
        "ItemRate",
        "ItemAmount",
        "ItemTaxCode",
        "ItemTaxAmount",
        "Currency",
        "ServiceDate",
      ],
    },
    (err, csv) => {
      if (err) {
        console.error("Export error:", err);
      } else {
        downloadCSV(csv, `invoice_services_${new Date().toLocaleDateString()}`);
      }
    },
  );
};

const shipmentTypeChoices = [
  { id: ChargeEvent.INBOUND, name: "Inbound" },
  { id: ChargeEvent.OUTBOUND, name: "Outbound" },
];

const dateTypeChoices = [
  { id: "serviceDate", name: "Service Date" },
  { id: "createdAt", name: "Invoice Created Date" },
];

const FiltersToolbar = (props) => {
  const classes = useStyles();
  const mdDown = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const {
    account,
    setAccount,
    setAccountId,
    services,
    setServices,
    setSelectedServiceIds,
    shipmentType,
    setShipmentType,
    carrierType,
    setCarrierType,
    dateType,
    setDateType,
    year,
    setYear,
    monthRange,
    setMontRange,
    getReportData,
    loading,
  } = props;

  const handleAccountChange = async (selectedAccount) => {
    try {
      setAccount(selectedAccount);
      setAccountId(selectedAccount?.id || "");
      // Load services for the selected account
      if (selectedAccount?.id) {
        const svcList = await fetchServices(selectedAccount.id, true);
        setSelectedServiceIds([]);
        // keep only id & name for choices
        const choices = svcList.map((svc) => ({
          id: svc.id,
          name: svc.name,
        }));
        setServices(choices);
      } else {
        setServices([]);
        setSelectedServiceIds([]);
      }
    } catch (err) {
      logError("BillingDetailsReport:FiltersToolbar:handleAccountChange", err);
      setServices([]);
      setSelectedServiceIds([]);
    }
  };

  const handleDateTypeChange = (event) => {
    const value = event?.target?.value || "";
    setDateType(value);
    if (!value) setYear("");
  };

  return (
    <Box mt={-4} mb={1}>
      <TopToolbar>
        <HelpButton />
        <Button
          label="Export"
          color="primary"
          onClick={() => {
            if (props?.finalData?.length > 0) {
              exportList(props.finalData);
            }
          }}
          disabled={
            loading || !props?.finalData || props.finalData.length === 0
          }
        >
          <ExportIcon />
        </Button>
        <BackButton basePath="/reports" />
      </TopToolbar>
      <Grid container spacing={1}>
        <Grid item xs={12} md={3}>
          <AccountDropDown
            source="accountId"
            label="Account"
            onAccountChange={handleAccountChange}
            allowEmpty
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <AutocompleteArrayInput
            source="serviceIds"
            label="Services"
            variant="outlined"
            fullWidth
            choices={services}
            optionText="name"
            optionValue="id"
            onChange={(valueOrEvent) => {
              const nextValue = Array.isArray(valueOrEvent)
                ? valueOrEvent
                : valueOrEvent?.target?.value || [];
              setSelectedServiceIds(nextValue);
            }}
            options={{
              InputProps: {
                className: classes.servicesAutocompleteRoot,
              },
            }}
            helperText={false}
            disabled={!account?.id || !services?.length}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <SelectInput
            source="shipmentType"
            label="Shipment Type"
            variant="outlined"
            fullWidth
            choices={shipmentTypeChoices}
            allowEmpty
            optionText="name"
            optionValue="id"
            value={shipmentType}
            onChange={(event) => setShipmentType(event?.target?.value || "")}
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <SelectInput
            source="carrierType"
            label="Carrier Type"
            variant="outlined"
            fullWidth
            choices={CarrierTypes}
            allowEmpty
            value={carrierType}
            onChange={(event) =>
              setCarrierType(event?.target?.value || undefined)
            }
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <SelectInput
            source="dateType"
            label="Date Type"
            variant="outlined"
            fullWidth
            choices={dateTypeChoices}
            value={dateType}
            onChange={handleDateTypeChange}
            allowEmpty
            resettable
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <YearSelect
            year={year}
            setYear={setYear}
            disabled={!dateType}
            validate={dateType ? required() : undefined}
            allowEmpty
            resettable
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box mt={-1.5} ml={1}>
            <MonthSlider
              maxMonths={12}
              monthRange={monthRange}
              setMontRange={setMontRange}
              disabled={!dateType || !year}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={1} container justifyContent="flex-end">
          <Box mt={1.5}>
            <Button
              label="Run"
              variant="contained"
              color="default"
              onClick={getReportData}
              disabled={loading || (dateType && !year)}
              size={mdDown ? "small" : "medium"}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// /reportInvoiceServices
export const BillingDetailsReport = (props) => {
  const notify = useNotify();

  const [account, setAccount] = useState(null);
  const [accountId, setAccountId] = useState("");
  const [services, setServices] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [shipmentType, setShipmentType] = useState("");
  const [carrierType, setCarrierType] = useState();
  const [dateType, setDateType] = useState("");
  const [year, setYear] = useState();
  const [monthRange, setMontRange] = useState([1, 1]);

  const [finalData, setFinalData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fixedFilter = "";
  const defaultSort = { field: "serviceDate", order: "DESC" };
  const defaultPerPage = 25;

  const listContext = useList({
    ...props,
    data: finalData,
    ids: finalData.map(({ id }) => id),
    loading,
    loaded: !loading,
    filter: fixedFilter,
    sort: defaultSort,
    perPage: defaultPerPage,
  });

  const getReportData = async () => {
    try {
      setLoading(true);

      const andConditions = [];

      if (window.appUser?.reloadYardId) {
        andConditions.push({
          reloadYardId: { eq: window.appUser.reloadYardId },
        });
      }

      if (accountId) {
        andConditions.push({ accountId: { eq: accountId } });
      }

      if (selectedServiceIds?.length) {
        andConditions.push({
          or: selectedServiceIds.map((id) => ({ serviceId: { eq: id } })),
        });
      }

      if (shipmentType === ChargeEvent.INBOUND) {
        andConditions.push({
          or: [
            { inboundShipmentId: { exists: true } },
            { event: { eq: ChargeEvent.INBOUND } },
          ],
        });
      } else if (shipmentType === ChargeEvent.OUTBOUND) {
        andConditions.push({
          or: [
            { outboundShipmentId: { exists: true } },
            { event: { eq: ChargeEvent.OUTBOUND } },
          ],
        });
      }

      if (dateType && year) {
        const { startDate, endDate } = createDateRangeParams(year, monthRange);
        const dateField = dateType;
        andConditions.push({ [dateField]: { gte: `${startDate}` } });
        andConditions.push({ [dateField]: { lt: `${endDate}` } });
      }

      const filter =
        andConditions.length === 1
          ? andConditions[0]
          : andConditions.length > 1
            ? { and: andConditions }
            : undefined;

      const limit = 1000;
      let nextToken = null;
      const items = [];

      do {
        const result = await API.graphql(
          graphqlOperation(searchInvoiceServicesForReport, {
            filter,
            sort: { field: "serviceDate", direction: "desc" },
            limit,
            nextToken,
          }),
        );
        const data = result.data.searchInvoiceServices;
        if (data?.items?.length) {
          items.push(...data.items);
        }
        nextToken = data?.nextToken || null;
      } while (nextToken);

      // Local filters that can't be expressed in searchInvoiceServices
      const filtered = items.filter((line) => {
        // Carrier type filter (applies to either inbound or outbound shipment)
        if (carrierType) {
          if (shipmentType === ChargeEvent.INBOUND) {
            const ct = line?.inboundShipment?.carrierType;
            if (ct !== carrierType) return false;
          } else if (shipmentType === ChargeEvent.OUTBOUND) {
            const ct = line?.outboundShipment?.carrierType;
            if (ct !== carrierType) return false;
          } else {
            const ct =
              line?.inboundShipment?.carrierType ||
              line?.outboundShipment?.carrierType;
            if (ct !== carrierType) return false;
          }
        }

        return true;
      });

      setFinalData(filtered);
    } catch (err) {
      logError("BillingDetailsReport:getReportData", err, notify);
      setFinalData([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SimpleForm toolbar={false}>
      <ListContextProvider value={listContext}>
        <Title title={<ForceTitle title="Billing Details" />} />
        <FiltersToolbar
          {...props}
          account={account}
          setAccount={setAccount}
          setAccountId={setAccountId}
          services={services}
          setServices={setServices}
          setSelectedServiceIds={setSelectedServiceIds}
          shipmentType={shipmentType}
          setShipmentType={setShipmentType}
          carrierType={carrierType}
          setCarrierType={setCarrierType}
          dateType={dateType}
          setDateType={setDateType}
          year={year}
          setYear={setYear}
          monthRange={monthRange}
          setMontRange={setMontRange}
          getReportData={getReportData}
          loading={loading}
        />

        <Card>
          <InvoiceServicesGrid {...props} {...listContext} />
          <PaginationToolbar {...props} finalData={finalData} />
        </Card>
      </ListContextProvider>
    </SimpleForm>
  );
};

const PaginationToolbar = (props) => {
  const classes = useStyles();
  const listContext = useListContext(props);

  var formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",

    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  const [totals, setTotals] = useState({ amount: 0, tax: 0, total: 0 });

  useEffect(() => {
    const { finalData = [] } = props;
    if (finalData.length > 0) {
      let amount = 0;
      let tax = 0;
      let total = 0;

      finalData.forEach((record) => {
        amount += record.amount || 0;
        tax += record.itemTax || 0;
        total += record.itemTotal || 0;
      });

      setTotals({ amount, tax, total });
    } else {
      setTotals({ amount: 0, tax: 0, total: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.finalData]);

  return (
    <Grid container direction="row" spacing={1} alignItems="center">
      <Hidden mdDown>
        <Grid item xs={5}>
          <Typography className={classes.totals}>
            {`AMOUNT: ${formatter.format(
              totals.amount,
            )}, TAX: ${formatter.format(totals.tax)}, TOTAL: ${formatter.format(
              totals.total,
            )}`}
          </Typography>
        </Grid>
      </Hidden>
      <Grid item xs={12} lg={7}>
        <Pagination {...props} {...listContext} />
      </Grid>
    </Grid>
  );
};

const InvoiceServicesGrid = ({ ids, page, ...props }) => {
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  return isXSmall ? (
    <SimpleList
      primaryText={(record) => record.invoice?.accountName}
      secondaryText={(record) =>
        `Invoice No: ${record.invoice?.invoiceNumber}    Item Total: $${
          record.itemTotal || 0
        }`
      }
      tertiaryText={(record) =>
        record.serviceDate
          ? new Date(record.serviceDate).toLocaleDateString()
          : ""
      }
      rightIcon={() => (
        <ChevronRightIcon
          fontSize="large"
          color="secondary"
          style={{ marginLeft: 40, marginTop: 10 }}
        />
      )}
      linkType={false}
    />
  ) : (
    <Datagrid>
      <TextField source="invoice.accountName" label="Account" sortable={true} />
      <TextField
        source="invoice.invoiceNumber"
        label="Invoice No."
        sortable={true}
      />
      <TextField source="service.name" label="Service" sortable={true} />
      <TextField
        source="inboundShipment.carrierType"
        label="Inbound Carrier"
        sortable={false}
      />
      <TextField
        source="outboundShipment.carrierType"
        label="Outbound Carrier"
        sortable={false}
      />
      <DateField source="serviceDate" label="Service Date" sortable={true} />
      <NumberField
        source="amount"
        label="Amount"
        sortable={true}
        options={{
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
        }}
      />
      <NumberField
        source="itemTax"
        label="Tax"
        sortable={true}
        options={{
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
        }}
      />
      <NumberField
        source="itemTotal"
        label="Total"
        sortable={true}
        options={{
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
        }}
      />
    </Datagrid>
  );
};
